import type {
  AcademyModule, AcademyChapter, AcademyQuestion, AcademyTrack,
} from '@platform/shared-types';

// Self-contained client for academy-service (video course library + exam engine
// behind affiliate certification).
// Mirrors hardwareApi.ts conventions — same shared `platform` cluster host
// resolution, same auth header source.
//
// NOTE the host shape: this service lives on the SHARED `platform` cluster and
// is addressed by BASE PATH (/academy), not a per-service subdomain. Do not
// "correct" this to academy.api.<apex> — that pattern is closed (AWS domain cap).

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveAcademyHost(
  hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string,
): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/academy-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/academy`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/academy`;
  return `https://platform.api.dev.${apex}/academy`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_ACADEMY_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveAcademyHost(h, detectStage(), envBase);
};

// Same auth source as hardwareApi.ts / billingApi.ts / deviceConfigApi.ts:
// super-admin-ui stores the admin session token under 'jwtToken' and sends it
// bare (no "Bearer " prefix). platform-app ALSO stores its session under the
// key 'jwtToken' (services/apiService.ts does
// localStorage.setItem('jwtToken', session.idToken)) — the key is the same
// in both apps; only the underlying Cognito token differs by pool.
function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    // Surface the server's own message — validation errors from the service are
    // the most useful thing a user can see here.
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error || '';
    } catch { /* non-JSON body */ }
    throw new Error(detail || `academy ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface ModuleWithChapters { module: AcademyModule; chapters: AcademyChapter[] }

export const academyApi = {
  listModules: () => call<{ modules: ModuleWithChapters[] }>('/admin/modules'),

  saveModule: (moduleId: string, module: Partial<AcademyModule>, chapters: AcademyChapter[]) =>
    call<ModuleWithChapters>(`/admin/modules/${encodeURIComponent(moduleId)}`, {
      method: 'PUT', body: JSON.stringify({ module, chapters }),
    }),

  deleteModule: (moduleId: string) =>
    call<{ deleted: string }>(`/admin/modules/${encodeURIComponent(moduleId)}`, { method: 'DELETE' }),

  listQuestions: (chapterId: string) =>
    call<{ questions: AcademyQuestion[] }>(`/admin/chapters/${encodeURIComponent(chapterId)}/questions`),

  /**
   * Replaces the whole question set for this chapter. The backend rejects with
   * 400 unless `questions` is an array — never call this with `undefined`.
   */
  saveQuestions: (chapterId: string, questions: AcademyQuestion[]) =>
    call<{ questions: AcademyQuestion[] }>(`/admin/chapters/${encodeURIComponent(chapterId)}/questions`, {
      method: 'PUT', body: JSON.stringify({ questions }),
    }),

  presignVideo: (input: {
    moduleId: string; chapterId: string; filename: string;
    contentType: string; sizeBytes: number;
  }) => call<{
    upload: { url: string; fields: Record<string, string> };
    video: { key: string; cdnUrl: string; contentType: string; sizeBytes: number; uploadedAt: number };
  }>('/admin/videos/upload-url', { method: 'POST', body: JSON.stringify(input) }),

  /**
   * Uploads straight to S3 via a presigned POST. This must NOT go through the
   * API — API Gateway caps a request body at 10 MB and these are videos up to
   * 2 GB. XHR rather than fetch because only XHR reports upload progress,
   * which the editor shows.
   */
  uploadVideo: (
    file: File,
    presigned: { url: string; fields: Record<string, string> },
    onProgress: (percent: number) => void,
  ) => new Promise<void>((resolve, reject) => {
    const form = new FormData();
    // The presigned POST policy already carries Content-Type (videoUpload.ts
    // signs it), and `file` MUST be the last field — S3 ignores anything after
    // it. Appending a second Content-Type on top of the signed one is
    // undefined behaviour for an S3 POST policy, so the returned fields go in
    // verbatim and nothing is added.
    Object.entries(presigned.fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', presigned.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('upload failed'));
    xhr.send(form);
  }),

  listTracks: () => call<{ tracks: AcademyTrack[] }>('/admin/tracks'),

  /** Soft-archives. The backend returns 409 for the activating certification. */
  archiveTrack: (trackId: string) =>
    call<{ trackId: string; archived: boolean }>(
      `/admin/tracks/${encodeURIComponent(trackId)}`,
      { method: 'DELETE' },
    ),

  getTrack: (trackId: string) =>
    call<{ track: AcademyTrack; bankSize: number }>(`/admin/tracks/${encodeURIComponent(trackId)}`),

  saveTrack: (trackId: string, track: Partial<AcademyTrack>) =>
    call<{ track: AcademyTrack }>(`/admin/tracks/${encodeURIComponent(trackId)}`, {
      method: 'PUT', body: JSON.stringify(track),
    }),

  getLearner: (learnerType: string, learnerId: string, trackId?: string) =>
    call<{ certification: any; completedChapterIds: string[]; attempts: any[] }>(
      `/admin/learners/${encodeURIComponent(learnerType)}/${encodeURIComponent(learnerId)}`
      + (trackId ? `?trackId=${encodeURIComponent(trackId)}` : ''),
    ),

  /**
   * Every learner's certification rows across every track — one row per
   * learner per certification actually earned. Backs the admin Partners tab
   * so it can report real per-certification data instead of the single
   * activation-time mirror on the affiliate record.
   */
  listCertifications: () => call<{
    certifications: {
      learnerType: string; learnerId: string; trackId: string;
      status: string; bestScorePercent?: number; passedAt?: number;
    }[];
  }>('/admin/certifications'),
};
